// ─── APP CONFIG DEFAULTS ──────────────────────────────────────────────────────

// Default coverage-request SMS templates. Placeholder tokens ({title},
// {description}, {date}, {time}, {time_to_event}) are filled by
// renderCoverageMessage (src/lib/sms.js). Editable in Settings → Caregivers &
// Access → Messages; existing families fall back to these constants.
//
// caregiverDecline uses a different token set ({parents}, {date},
// {time_range}) — rendered by renderCaregiverDeclineMessage (also
// src/lib/sms.js), since its input is a sessions doc, not a GCal event.
export const DEFAULT_COVERAGE_MESSAGES = {
  todayLate: "Hey, I was going to do {title} but I'm running late and likely won't make it — could I ask you to cover? It's at {time}, {time_to_event}.",
  future:    "Hey, could you help cover {title} on {date} at {time}? That's {time_to_event}. Let me know — thanks!",
  caregiverDecline: "Hi {parents} — I'm sorry, I won't be able to make it on {date} from {time_range}. Wanted to give you as much notice as possible so you can find coverage.",
};

export const DEFAULT_CONFIG = {
  // Feature toggles — off means the feature is built but not ready to surface yet
  features: {
    cashSignal: false,
    payManagement: true,
  },

  // Coverage-request message templates (editable in Settings → Messages)
  coverageMessages: DEFAULT_COVERAGE_MESSAGES,

  // General
  refreshOnLaunch: true,
  refreshOnTabSelect: true,
  refreshOnRangeChange: true,
  dataStaleWarningMinutes: 30,

  // Caregiver rates — canonical shape, matches what Settings → Rates
  // (RatesSettings in SettingsModule.jsx) actually writes. The
  // baseRateUpToTwoKids/baseRateThreePlusKids fields below are kept only as
  // a fallback for calculateSessionPay when config.rates hasn't been set.
  rates: { twoKids: 25, threeKids: 30, cashThreshold: 50 },
  baseRateUpToTwoKids: 25,
  baseRateThreePlusKids: 30,
  sittingCashThreshold: 50,
  payRoundingIncrement: 5,

  // Care Partner identifiers
  carePartnerIdentifiers: {}, // { uid: 'A' | 'K' | custom }

  // Notifications — global defaults
  notificationChannels: ['system'], // 'system' | 'imessage' | 'email' | 'disabled'

  // Keyword registries
  keywordRegistries: {
    workModes: [
      { keyword: 'Work', enabled: true },
      { keyword: 'Office', enabled: true },
      { keyword: 'Clinical', enabled: true },
      { keyword: 'Clinic', enabled: true },
      { keyword: 'Travel', enabled: true },
      { keyword: 'Conference', enabled: true },
      { keyword: 'Offsite', enabled: true },
    ],
    auxiliaryCare: [
      { keyword: 'Bright Horizons', enabled: true },
      { keyword: 'BH Care', enabled: true },
      { keyword: 'Back-Up Care', enabled: true },
      { keyword: 'AKP', enabled: true },
      { keyword: 'Au Pair', enabled: true },
      { keyword: 'Grampy', enabled: true },
      { keyword: 'Deedle', enabled: true },
    ],
    schoolSignals: [
      { keyword: 'No School', enabled: true },
      { keyword: 'Day Off', enabled: true },
      { keyword: 'Holiday', enabled: true },
      { keyword: 'Early Release', enabled: true },
      { keyword: 'Half Day', enabled: true },
    ],
    reminderSignals: [
      { keyword: 'reminder: yes', enabled: true },
    ],
  },

  // Vacation overlap rules
  vacationOverrideRules: {
    alwaysWarn: ['medical', 'one_off_appointment', 'tutoring'],
    lowImpact: ['recurring_activity'],
    ignoreList: [], // custom event titles/keywords to never flag
  },
};

// ─── SCHEDULE CONVENTION OPTIONS ─────────────────────────────────────────────

export const SCHEDULE_CONVENTIONS = {
  NEVER_SCHEDULED: 'standing_window',
  ALWAYS_SCHEDULED: 'always_scheduled',
  MIXED: 'mixed',
};

// ─── COVERAGE ASSUMPTIONS ─────────────────────────────────────────────────────

export const COVERAGE_ASSUMPTIONS = {
  FLAG_WINDOW: 'flag_window',       // Flag events during unavailable window only
  FLAG_BEFORE_HOME: 'flag_before_home', // Flag before estimated home time
  FLAG_ALL_DAY: 'flag_all',         // Flag all kid events that day
};

// ─── ALERT LEVELS ────────────────────────────────────────────────────────────

export const ALERT_LEVELS = {
  HIGH: 'high',
  MEDIUM: 'medium',
  INFO: 'info',
  CLEAR: 'clear',
};

export const ALERT_COLORS = {
  high: '#DC2626',    // red
  medium: '#D97706',  // amber
  info: '#2563EB',    // blue
  clear: '#16A34A',   // green
};

// ─── CAREGIVER BOOKING ACTIVITY TYPES ──────────────────────────────────────────
// The "What" half of a caregiver booking's When/What section — optional, on
// top of the required Helper/Sitter "When" mode. 'dogs_only' is a built-in id
// with special handling in ScheduleSitter (hides the kids picker); the rest
// are just labels. Editable in Settings → Caregivers & Access → Activities,
// same disabled/custom-additions pattern as DEFAULT_REGISTRIES.

export const DEFAULT_ACTIVITY_TYPES = [
  { id: 'jiu_jitsu_balance', label: 'Jiu Jitsu @ Balance' },
  { id: 'swim',              label: 'Swim' },
  { id: 'gymnastics',        label: 'Gymnastics' },
  { id: 'dogs_only',         label: 'Dogs Only' },
];

// Effective activity-type list for a family — defaults minus anything
// disabled, plus custom additions. Custom entries have no separate id (same
// convention DEFAULT_REGISTRIES' custom keywords use); the label doubles as
// the id.
export function resolveActivityTypes(config) {
  const saved = config?.activityTypes || {};
  const disabled = new Set(saved.disabled || []);
  return [
    ...DEFAULT_ACTIVITY_TYPES.filter(a => !disabled.has(a.id)),
    ...(saved.custom || []).map(label => ({ id: label, label })),
  ];
}

// ─── EVENT TYPES ──────────────────────────────────────────────────────────────

export const EVENT_TYPES = {
  GENERAL: 'general',
  MEDICAL: 'medical',
  TRAVEL: 'travel',
  VACATION: 'vacation',
  OTHER: 'other',
  SITTER: 'sitter',
  WORK_TRAVEL: 'work_travel',
  ADD_SUPPORT: 'add_support',
};

// ─── SITTER SESSION STATUS ────────────────────────────────────────────────────
// 'declined' = the caregiver themselves cancelled (SessionCancelSheet).
// 'cancelled' = the family cancelled it from Review & Assign (EventCard's
// cancel flow, on Lock In) — kept distinct from 'declined' since the family
// initiated it, not the caregiver.

export const SESSION_STATUS = {
  SCHEDULED: 'scheduled',
  DECLINED: 'declined',
  CANCELLED: 'cancelled',
  COMPLETED: 'completed',
};

// ─── CAREGIVER-SESSION HINTS ON REVIEW & ASSIGN ───────────────────────────────
// Temporarily suppressed: the "Caregiver sessions" chip strip, day-level 🌱
// badge, and the overlap-based quick-assign button (which wrongly implied a
// booked caregiver owns any event inside their time window — that can't be
// assumed). Declined-session banners are unaffected by this flag. Code stays
// in place — flip back to true once the overlap assumption is reworked.
export const SHOW_CAREGIVER_SESSION_HINTS = false;

// ─── CANCEL REASONS ───────────────────────────────────────────────────────────

export const CANCEL_REASONS = [
  { id: 'too_busy', label: 'Too Busy' },
  { id: 'schedule_conflict', label: 'Schedule Conflict' },
  { id: 'taking_a_break', label: 'Taking a Break' },
  { id: 'other', label: 'Other' },
];

// ─── LOGISTICS VIEW RANGES ────────────────────────────────────────────────────

export const VIEW_RANGES = [
  { label: 'This Week', value: 'this_week' },
  { label: '+1 Week', value: 'plus_1_week' },
  { label: '+2 Weeks', value: 'plus_2_weeks' },
  { label: '+3 Weeks', value: 'plus_3_weeks' },
];

/**
 * Calculates the dynamic default view range.
 * Default: today → end of next full week (Mon–Sun).
 * "If you look on Wed Mar 11, you see Mar 11 → Mar 22."
 */
export function getDefaultViewRange(fromDate = new Date()) {
  const start = new Date(fromDate);
  start.setHours(0, 0, 0, 0);

  // Find end of next full week (next Sunday)
  const dow = start.getDay(); // 0=Sun
  const daysUntilEndOfThisWeek = 7 - dow; // days until next Sunday
  const end = new Date(start);
  end.setDate(start.getDate() + daysUntilEndOfThisWeek + 7); // + one more full week
  end.setHours(23, 59, 59, 999);

  return { start, end };
}

/**
 * Calculates start/end for a given view range option.
 */
export function getViewRangeDates(rangeValue, fromDate = new Date()) {
  const { start } = getDefaultViewRange(fromDate);
  const end = new Date(start);

  switch (rangeValue) {
    case 'this_week': {
      const dow = start.getDay();
      end.setDate(start.getDate() + (7 - dow));
      break;
    }
    case 'plus_1_week':
      end.setDate(start.getDate() + 14);
      break;
    case 'plus_2_weeks':
      end.setDate(start.getDate() + 21);
      break;
    case 'plus_3_weeks':
      end.setDate(start.getDate() + 28);
      break;
    default:
      end.setDate(start.getDate() + 14);
  }
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

// ─── PAY CALCULATION ──────────────────────────────────────────────────────────

// rateOverride: a caregiver's own rateOverride (null = use the family
// default below; 0 = unpaid/family caregiver — callers should check for 0
// separately if they want to show "Unpaid" instead of a computed number).
export function calculateSessionPay(durationMinutes, numKids, config = DEFAULT_CONFIG, rateOverride = null) {
  const rates = config?.rates || {};
  const rate = rateOverride !== null
    ? rateOverride
    : numKids >= 3
      ? (rates.threeKids ?? config?.baseRateThreePlusKids ?? DEFAULT_CONFIG.baseRateThreePlusKids)
      : (rates.twoKids   ?? config?.baseRateUpToTwoKids   ?? DEFAULT_CONFIG.baseRateUpToTwoKids);

  const hours = durationMinutes / 60;
  const raw = hours * rate;
  const increment = config?.payRoundingIncrement || 5;
  return Math.ceil(raw / increment) * increment;
}

// ─── WEEK UTILITIES ───────────────────────────────────────────────────────────

export function getWeekBounds(date = new Date()) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const dow = d.getDay();
  const monday = new Date(d);
  monday.setDate(d.getDate() - (dow === 0 ? 6 : dow - 1));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  return { monday, sunday };
}

export function getThisWeekBounds() {
  return getWeekBounds(new Date());
}

export function getNextWeekBounds() {
  const next = new Date();
  next.setDate(next.getDate() + 7);
  return getWeekBounds(next);
}

export function formatWeekLabel(monday, sunday) {
  const opts = { month: 'numeric', day: 'numeric' };
  return `${monday.toLocaleDateString('en-US', opts)} – ${sunday.toLocaleDateString('en-US', opts)}`;
}
